import { readItem, removeItem, writeItem } from "./safeStorage";

/**
 * Параметры, с которыми человек пришёл: ?ref= (ссылка креатора или партнёра — сервер
 * запоминает касание, POST /touch), ?code= (код, который вводят при регистрации и
 * оплате) и, в разработке, ?hero= (какой герой, раз на localhost нет поддомена).
 *
 * Оба живут в sessionStorage: визит — правильная единица жизни. Код в localStorage
 * прицепился бы к оплате через полгода по забытой ссылке, и процент ушёл бы не тому.
 * Из адреса оба убираются сразу: иначе код уедет в закладку и в ссылку, которой
 * человек поделится, и чужое приглашение начнёт размножаться от его имени.
 */

const CODE_KEY = "scrile.code";
const HERO_KEY = "scrile.dev-hero";

/** Код — латиница, цифры, дефис и подчёркивание, до 32 знаков (как RegisterRequest.code). */
export const CODE_SHAPE = /^[A-Z0-9_-]{2,32}$/;
/** Код ссылки — от 4 знаков (TouchRequest.code). */
const REF_SHAPE = /^[A-Z0-9_-]{4,32}$/;
/**
 * Переход по карточке героя: `?ref=hero:<slug отправителя>` (сервер ставит его в ссылку
 * карточки). Слаг — нижний регистр, как поддомен: в верхний его не переводим, иначе
 * сервер не узнал бы героя, приславшего карточку. Длина — в пределах TouchRequest.code.
 */
const HERO_REF_SHAPE = /^hero:([a-z0-9](?:[a-z0-9-]{0,56}[a-z0-9])?)$/;
const SLUG_SHAPE = /^[a-z0-9][a-z0-9-]{0,62}$/;

export function normalizeCode(raw: string): string | null {
  const code = raw.trim().toUpperCase();
  return CODE_SHAPE.test(code) ? code : null;
}

/** Код ссылки для POST /touch: код креатора или партнёра (верхний регистр) или карточка героя. */
export function normalizeRef(raw: string): string | null {
  const trimmed = raw.trim();
  const card = HERO_REF_SHAPE.exec(trimmed.toLowerCase());
  if (card) return `hero:${card[1]}`;
  const code = trimmed.toUpperCase();
  return REF_SHAPE.test(code) ? code : null;
}

export interface Landing {
  /** Очищенный адрес для history.replaceState; null — чистить нечего. */
  cleaned: string | null;
  /** Код ссылки для POST /touch; null — пришли не по ссылке. */
  ref: string | null;
}

/**
 * Снимает ?ref=, ?code= и ?hero= с адреса. Код и героя запоминает на вкладку; код ссылки
 * возвращает — отправить касание должен вызывающий, когда API готов.
 */
export function captureLanding(href: string, options: { allowHeroOverride: boolean }): Landing {
  const url = new URL(href);
  let changed = false;
  let ref: string | null = null;

  const rawRef = url.searchParams.get("ref");
  if (rawRef !== null) {
    ref = normalizeRef(rawRef);
    url.searchParams.delete("ref");
    changed = true;
  }

  const code = url.searchParams.get("code");
  if (code !== null) {
    const normalized = normalizeCode(code);
    if (normalized) writeItem("session", CODE_KEY, normalized);
    url.searchParams.delete("code");
    changed = true;
  }

  const hero = url.searchParams.get("hero");
  if (hero !== null && options.allowHeroOverride) {
    const slug = hero.trim().toLowerCase();
    if (SLUG_SHAPE.test(slug)) writeItem("session", HERO_KEY, slug);
    url.searchParams.delete("hero");
    changed = true;
  }

  return { cleaned: changed ? url.pathname + url.search + url.hash : null, ref };
}

export function takeCode(): string | null {
  return readItem("session", CODE_KEY);
}

export function rememberCode(raw: string): void {
  const normalized = normalizeCode(raw);
  if (normalized) writeItem("session", CODE_KEY, normalized);
  else removeItem("session", CODE_KEY);
}

/** Герой в разработке: ?hero= этой вкладки, иначе VITE_HERO. */
export function devHeroSlug(fallback: string): string | null {
  const stored = readItem("session", HERO_KEY);
  if (stored && SLUG_SHAPE.test(stored)) return stored;
  return fallback && SLUG_SHAPE.test(fallback) ? fallback : null;
}
